import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Search, LogOut, Settings } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { NotificationDropdown } from './NotificationDropdown'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'
import { useScrollHide } from '@/hooks/useScrollHide'
import { useWorkspace } from '@/lib/workspace'
import { usePermissions } from '@/hooks/usePermission'
import { displayName } from './account'
import { WorkspaceMark } from './WorkspaceMark'
import { BusinessName } from './BusinessName'

function UserMenu() {
  const [open, setOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const { roleLabel } = usePermissions()
  const navigate = useNavigate()
  // Same name source as the desktop sidebar (UX-208): no "[DEMO]" prefix here
  // when the desktop leaves it off.
  const name = displayName(user?.name)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="User menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-1.5 py-1 min-h-11 rounded-full hover:bg-white/[0.04] transition-colors"
      >
        <Avatar name={name} size="sm" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 glass-popup py-1 z-50 animate-popover-in">
            <div className="px-3 py-2 hairline-b mb-1">
              <p className="text-sm font-medium text-ink-100 truncate">{name}</p>
              <p className="text-xs text-white/[0.62] truncate">{roleLabel} · {user?.email}</p>
            </div>
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-sm text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] transition-colors"
            >
              <Settings className="w-4 h-4" /> Settings
            </Link>
            <div className="my-1 border-t border-white/[0.06]" />
            <button
              onClick={async () => { setOpen(false); await logout(); navigate('/login', { state: { signedOut: true } }) }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#E1A29A] hover:text-[#EFBFB9] hover:bg-[#A84B3E]/10 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export function Topbar() {
  const workspace = useWorkspace()
  const setSearchOpen = useUIStore((s) => s.setSearchOpen)
  const headerHidden = useScrollHide()

  return (
    <header
      // Phone only (<md): a fixed overlay so translateY(-100%) vacates the
      // viewport without layout shift. From md up the sidebar carries search,
      // notifications and the account menu, so this header is not rendered.
      className={cn(
        // Opaque midnight already. A blur here samples the scrolling page
        // underneath; on iPhone Safari that drops the scroller's tiles and
        // the content flashes black while this bar stays painted.
        'md:hidden flex items-center gap-1 hairline-b shrink-0',
        'fixed top-0 left-0 right-0 z-50',
        // Smooth hide/show — disabled when user prefers reduced motion
        'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-in-out',
        // Hide when scrolling down — but never while one of its buttons has
        // focus, or a keyboard user tabs onto controls that are off-screen.
        headerHidden && '-translate-y-full focus-within:translate-y-0',
      )}
      // Reads safe-area insets via the global :root CSS vars in index.css
      // (--safe-top / --safe-left / --safe-right). The vars are the canonical
      // way to consume the insets in styles where wrapping with SafeAreaTop /
      // SafeAreaInsets isn't structurally clean (the <header> needs its own
      // height + padding). CLAUDE.md §8.
      style={{
        height: 'calc(3.5rem + var(--safe-top))',
        paddingTop: 'var(--safe-top)',
        paddingLeft: 'max(1.5rem, var(--safe-left))',
        paddingRight: 'max(1.5rem, var(--safe-right))',
        background: 'var(--midnight-bg, rgb(var(--bg-rgb)))',
      }}
    >
      {/* The business, named (UX-208). No hamburger: the dock and its More
          sheet are the one phone navigation (UX-112). */}
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <WorkspaceMark name={workspace.name} className="w-8 h-8" />
        <BusinessName name={workspace.name} className="min-w-0 truncate text-[17px] font-semibold tracking-[-0.01em] text-white" />
      </div>


      {/* Search — opens the global search modal (mounted in Layout) */}
      <button
        onClick={() => setSearchOpen(true)}
        className="w-11 h-11 flex items-center justify-center text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-all shrink-0"
        aria-label="Open global search"
      >
        <Search className="w-5 h-5" />
      </button>
      {/* Notifications */}
      <NotificationDropdown />

      {/* User */}
      <UserMenu />
    </header>
  )
}
